import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchCustomer } from '../services/recharge';
import {
  ensureAssignment,
  getBucket,
  getRolloutArm,
  getActiveVersion,
  hasGivenFeedback,
  isAssigned,
  applyDesignOverride,
  clearDesignOverride,
  setActiveVersion as setActiveVersionSvc,
  submitRedesignFeedback as submitFeedbackSvc,
  VERSION,
  REDESIGN_VERSION_EVENT,
  FORCE_OLD_DESIGN,
} from '../services/redesignExperiment';

/**
 * Single entry point for the redesign A/B experiment in the UI.
 *
 * - On mount: bucket the member (once) using their Recharge account creation
 *   date vs the launch-date cutoff.
 * - Keeps `activeVersion` / `feedbackGiven` in sync via the shared
 *   `wg-redesign-version-changed` window event, so the sidebar and any page
 *   consuming this hook always agree.
 * - Exposes `switchVersion` and `submitFeedback`, both of which need the
 *   member's email (captured here) to emit tracking.
 */
export default function useRedesignExperiment() {
  const [email, setEmail] = useState(null);
  const [bucket, setBucket] = useState(getBucket());
  const [rolloutArm, setRolloutArm] = useState(getRolloutArm());
  const [activeVersion, setActiveVersion] = useState(getActiveVersion());
  const [feedbackGiven, setFeedbackGiven] = useState(hasGivenFeedback());
  const [assignmentSettled, setAssignmentSettled] = useState(false);
  const assignedRef = useRef(isAssigned());

  // Resolve the member and ensure they're bucketed (idempotent).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const customer = await fetchCustomer();
        if (cancelled || !customer?.email) return;
        setEmail(customer.email);

        const result = await ensureAssignment({
          email: customer.email,
          createdAt: customer.created_at,
        });
        if (result && !cancelled) {
          assignedRef.current = true;
          setBucket(result.bucket);
          setRolloutArm(result.rolloutArm || null);
          setActiveVersion(result.activeVersion);
        }
      } catch (err) {
        // Not logged in / session issue — leave the member unbucketed; the
        // widget simply won't render.
        console.warn('useRedesignExperiment: could not resolve member', err);
      } finally {
        // Releases the ?design= override below, whether or not assignment
        // succeeded — a failed lookup shouldn't strand the override forever.
        if (!cancelled) setAssignmentSettled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Stay in sync with switches / feedback fired from anywhere in the app.
  useEffect(() => {
    const handle = (e) => {
      setActiveVersion(getActiveVersion());
      setBucket(getBucket());
      setRolloutArm(getRolloutArm());
      if (e?.detail?.feedbackGiven) setFeedbackGiven(true);
    };
    window.addEventListener(REDESIGN_VERSION_EVENT, handle);
    return () => window.removeEventListener(REDESIGN_VERSION_EVENT, handle);
  }, []);

  // ?design=old|new in the URL performs a real, persisted switch — the same
  // effect as clicking the sidebar toggle (writes localStorage, syncs the
  // Klaviyo profile, fires the trackRedesignSwitch event), just triggerable
  // by URL/link instead of a click, and additionally pinned on this device so
  // it survives dropping the parameter. ?design=reset drops the pin.
  //
  // The pin is what makes this stick for CONTROL members: they get no
  // test_assignments row until they switch, so every assignment read used to
  // answer `old` and undo it the moment the parameter left the URL.
  //
  // Waits for `assignmentSettled`, not just for email: ensureAssignment applies
  // whatever the API says the member's current version is, so switching before
  // it returns had the response land afterwards and silently undo the override.
  useEffect(() => {
    if (!email || !assignmentSettled) return;
    const design = new URLSearchParams(window.location.search).get('design');
    if (design === 'reset' || design === 'auto') {
      clearDesignOverride();
      return;
    }
    if (design !== 'old' && design !== 'new') return;
    applyDesignOverride(email, design === 'new' ? VERSION.NEW : VERSION.OLD);
  }, [email, assignmentSettled]);

  const switchVersion = useCallback(
    (next, source = 'sidebar') => setActiveVersionSvc(email, next, source),
    [email]
  );

  const submitFeedback = useCallback(
    ({ rating, comment }) => {
      setFeedbackGiven(true); // optimistic; service also persists the flag
      return submitFeedbackSvc({ email, rating, comment });
    },
    [email]
  );

  return {
    isAssigned: assignedRef.current || !!bucket,
    bucket,
    rolloutArm,
    activeVersion,
    isTestVersion: !FORCE_OLD_DESIGN && activeVersion === VERSION.NEW,
    feedbackGiven,
    email,
    assignmentSettled,
    switchVersion,
    submitFeedback,
  };
}
